import { Box } from "@mui/material";
import type { ReactNode } from "react";

import logoFull from "../../../logo-full.svg";
import {
	CoAppLoginCard,
	CoAppLoginContainer,
	CoAppLoginFooter
} from "../../global/styled/global.styled";

interface LoginChromeProps {
	/**
	 * When true, render the CoApp wordmark above the content. Default true.
	 * The polling / polling-success screens hide it so the message itself
	 * carries the visual weight.
	 */
	showLogo?: boolean;
	children: ReactNode;
}

export default function LoginChrome({ showLogo = true, children }: LoginChromeProps) {
	return (
		<CoAppLoginContainer>
			<CoAppLoginCard>
				{showLogo && (
					<Box
						component="img"
						src={logoFull}
						alt="CoApp"
						sx={{
							width: 168,
							height: "auto",
							margin: "0 auto 4px",
							display: "block",
							pointerEvents: "none",
							userSelect: "none"
						}}
					/>
				)}

				{children}

				<CoAppLoginFooter>
					<a href="https://coapp.io/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
					<span aria-hidden="true">·</span>
					<a href="https://coapp.io/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
					<span aria-hidden="true">·</span>
					<a href="mailto:support@coapp.io">Support</a>
				</CoAppLoginFooter>
			</CoAppLoginCard>
		</CoAppLoginContainer>
	);
}

/**
 * Reusable primary gradient pill button — the visual twin of the Descope
 * "Continue" button defined in CoAppLoginFlowContainer. Lives here so each
 * prototype screen doesn't redeclare the gradient.
 */
export const PRIMARY_GRADIENT = "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)";

export const primaryButtonSx = {
	background: PRIMARY_GRADIENT,
	color: "#FFFFFF",
	borderRadius: "100px",
	fontWeight: 600,
	fontSize: 15,
	letterSpacing: "0.02em",
	padding: "10px 24px",
	minHeight: 44,
	textTransform: "none" as const,
	boxShadow: "0 4px 12px rgba(2, 213, 171, 0.20), 0 1px 4px rgba(10, 127, 168, 0.16)",
	transition: "filter 0.18s, box-shadow 0.18s, transform 0.06s",
	"&:hover": {
		background: PRIMARY_GRADIENT,
		filter: "brightness(1.08)",
		boxShadow: "0 6px 18px rgba(2, 213, 171, 0.32), 0 2px 6px rgba(10, 127, 168, 0.20)"
	},
	"&:active": {
		filter: "brightness(0.95)",
		transform: "translateY(1px)"
	},
	"&.Mui-disabled": {
		background: "rgba(13, 31, 48, 0.10)",
		color: "rgba(13, 31, 48, 0.40)",
		boxShadow: "none"
	}
};

export const secondaryButtonSx = {
	background: "transparent",
	color: "rgba(13, 31, 48, 0.85)",
	border: "1px solid rgba(13, 31, 48, 0.12)",
	borderRadius: "100px",
	fontWeight: 500,
	fontSize: 14,
	letterSpacing: "0.01em",
	padding: "9px 20px",
	minHeight: 42,
	textTransform: "none" as const,
	boxShadow: "none",
	transition: "background 0.18s, border-color 0.18s",
	"&:hover": {
		background: "rgba(2, 213, 171, 0.06)",
		borderColor: "rgba(2, 213, 171, 0.30)"
	}
};

export const textLinkSx = {
	color: "var(--coapp-text-interactive, #057A6D)",
	fontSize: 13,
	fontWeight: 500,
	textTransform: "none" as const,
	textDecoration: "none",
	padding: "4px 8px",
	minHeight: "auto",
	background: "transparent",
	"&:hover": {
		background: "transparent",
		textDecoration: "underline",
		textUnderlineOffset: 3
	}
};
